import { IAlertSummaryModel, INoteModel } from '../interfaces/INote';
import { IApiRequest, IApiResponse, ApiClient as TpiApiClient } from '../tpi.apiclient';
import {
    IAssertionDescriptorDisplayCategoryLookupModel,
    IDataScopeLookupModel,
    IDatabaseAppSettingModel,
    ILookupModel,
    IStaticItemLookupModel,
} from '../interfaces/ILookup';
import {
    IAssertionDescriptorExtendedModel,
    IAssertionDescriptorModel,
} from '../interfaces/IAssertionDescriptor';
import { ICaseAuditLog, ICasePlanTemplate, ICaseStatusLookupModel, IDepositionModel, IDocumentModel } from '../interfaces/ICase';
import {
    IInvitationModel,
    IRegistrationModel,
    IUserModel,
    IUserZoneProfileSearchModel,
} from '../interfaces/IUser';
import { DataScopesEnum, IQueryExportCountModel, LookupDataEnums } from '../utilities/Constants';
import {
    IRejectCaseSameCaseModel,
    ISameCaseCaseModel,
    ISameCaseModel,
    ISaveSameCaseModel,
} from '../interfaces/ISameCaseModel';
import { IResponse, IResponseModel } from '../interfaces/IResponse';
import { ISameCaseEasyUpdateModel, ISameCaseEasyUpdateResponseModel } from '../pages/quad/same-case-easy-update/ISameCaseEasyUpdateModel';
import {
    ISameCaseMatchResponseModel,
    ISameCaseMatchedCasesSearchModel,
    SameCaseSortColumns,
} from '../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';
import { ITaskCategoryModel, IUserSettingModel, IUserZoneProfileModel } from '../interfaces/ITask';

import { ApiRoutes } from '../utilities/ApiRoutes';
import { IAnnouncementModel } from '../interfaces/IAnnoucementModel';
import { ICaseFileModel } from '../interfaces/ICaseFileModel';
import { ICoordinates } from '../interfaces/ICoordinates';
import { IEntityLease } from '../interfaces/IEntityLease';
import { ILoginResponse } from '../interfaces/ILoginResponse';
import { ILoginUser } from '../interfaces/IAccount';
import { IMatchedCasesSearchResponseModel } from '../pages/quad/same-cases/view/IMatchedCasesSearchResponseModel';
import { IOrganizationModel } from '../interfaces/IOrganization';
import { IPagedSortedSearchResponseModel } from '../interfaces/PagedSortedSearch';
import { IQuadZoneNoteRequest } from '../interfaces/IQuad';
import { IRecoverAccount } from '../interfaces/IAccount';
import { config } from '../config/config';
import { IQuadTriggerRuleSettingsModel, ITriggerRuleSettingsModel } from '../interfaces/ITriggerRule';
import { INoteUISearchModel, IQueryResultModel, IQuerySettingsModel } from '../interfaces/IQuery';
import { ICaseExpertModel } from '../interfaces/ICaseExpert';
import { CreateCaseResolutionPlanRequest, ICaseResolutionCaseManager, ICaseResolutionPlaintiffFirms, ICaseResolutionResponseModel, ICaseResolutionSearchModel } from '../pages/quad/case-resolution-admin/useGetCaseResolutionQuery';
import { ICaseResolutionModel, ICaseResolutionPlanStepDataModel, ICaseResolutionPlanStepHistoryModel, ICaseResolutionPlanStepModel } from '../interfaces/ICaseResolutionModel';

class CheckmateApiClient extends TpiApiClient {
    constructor() {
        super(config.baseUrl);
    }

    /**
     * Prepares the request for transmission.
     * @param {ApiRequest} request The request object.
     * @returns {Promise<ApiRequest>} The prepared request object.
     */
    async prepareRequest<T>(request: IApiRequest<T>): Promise<IApiRequest<T>> {
        request = await super.prepareRequest(request);

        request.httpRequest.headers.set('User-Agent', 'CheckmateApiClient/1.0');
        request.httpRequest.headers.set('Credentials', 'same-origin');

        return request;
    }

    /**
     * Processes a response after completion.
     * @param {ApiRequest} request The request object.
     * @param {ApiResponse} response The response object.
     * @returns {Promise<ApiResponse>} The processed response object.
     */
    async processResponse<T>(
        request: IApiRequest<any>,
        response: IApiResponse<T>
    ): Promise<IApiResponse<T>> {
        response = await super.processResponse(request, response);
        return response;
    }

    // ===========================================================================
    // organization API Calls

    getDBAppSettings() {
        return this.get<IDatabaseAppSettingModel[]>(ApiRoutes.DBAppSettings);
    }

    getMasterSystemAdminSupportEmail() {
        return this.get<string>(ApiRoutes.MasterSystemAdminSupportEmail);
    }

    getUserAnnouncements() {
        return this.get<IAnnouncementModel[]>(ApiRoutes.GetUserAnnouncements);
    }

    getAnnouncement(announcementContentGuid: string) {
        return this.get<IAnnouncementModel>(
            ApiRoutes.GetAnnouncement.replace('{announcementContentGuid}', announcementContentGuid)
        );
    }

    getAnnouncements() {
        return this.get<IAnnouncementModel[]>(ApiRoutes.GetAnnouncements);
    }

    getPublicAnnouncements() {
        return this.get<IAnnouncementModel[]>(ApiRoutes.GetPublicAnnouncements);
    }

    acknowledgeAnnouncement(announcementContentGuid: string) {
        return this.post<IResponse>(
            ApiRoutes.AnnouncementUserAcknowledge.replace(
                '{announcementContentGuid}',
                announcementContentGuid
            )
        );
    }

    // LOOKUP
    getDataScopes() {
        return this.get<IDataScopeLookupModel[]>(ApiRoutes.Lookup + LookupDataEnums.DataScopes);
    }

    getUserRights() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + LookupDataEnums.UserRights);
    }

    getCaseStatusLookupData() {
        return this.get<ICaseStatusLookupModel[]>(
            ApiRoutes.Lookup + LookupDataEnums.CaseStatusTypes
        );
    }

    getCasePlanTemplates() {
        return this.get<ICasePlanTemplate[]>(ApiRoutes.Lookup + LookupDataEnums.CasePlanTemplates);
    }

    getTaskCategories() {
        return this.get<ITaskCategoryModel[]>(ApiRoutes.Lookup + LookupDataEnums.TaskCategories);
    }

    getAssociatedOrganizationsById(guid: string) {
        return this.get<IOrganizationModel[]>(
            ApiRoutes.AssociatedOrganizations.replace('{id}', guid)
        );
    }

    getLookupData(lookupType: LookupDataEnums) {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + lookupType);
    }

    getAdminLookupData(caseFileModelId: number, lookupType: LookupDataEnums) {
        return this.get<ILookupModel[]>(
            ApiRoutes.CaseFileModelLookup.replace(
                '{caseFileModelId}',
                caseFileModelId.toString()
            ).replace('{lookupType}', lookupType)
        );
    }

    getUserUnreadQueryExports() {
        return this.get<IQueryExportCountModel[]>(ApiRoutes.UnreadQueryExports);
    }

    getWeather(params: ICoordinates) {
        return this.get<IResponseModel<any>>(
            ApiRoutes.CurrentWeather + '?' + transformRequestOptions(params)
        );
    }

    getIsEntityLeaseAvailable(entityTypeId: number, entityGuid: string) {
        const url = ApiRoutes.EntityLeaseIsAvailable.replace('{entityId}', entityGuid).replace(
            '{entityTypeId}',
            entityTypeId.toString()
        );
        return this.get<IEntityLease>(url);
    }

    // ASSERTION DESCRIPTOR
    getAssertionDescriptorDispositionType() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/assertiondescriptordispositiontypes');
    }

    getAssertionDescriptorCategories() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/assertiondescriptorcategories');
    }

    getAssertionDescriptorValueSourceType() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/assertiondescriptorvaluesourcetypes');
    }

    getAssertionDescriptorValueDisplayType() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/assertiondescriptorvaluedisplaytypes');
    }

    getAssertionDescriptorUsageTypes() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/assertiondescriptorusagetypes');
    }

    // ADMIN
    createAssertionDescriptor(assertionDescriptor: IAssertionDescriptorModel) {
        const body: IAssertionDescriptorModel = assertionDescriptor;
        return this.post<IAssertionDescriptorModel>(ApiRoutes.Assertiondescriptor, {
            body,
        });
    }

    updateAssertionDescriptor(assertionDescriptor: IAssertionDescriptorModel) {
        const body: IAssertionDescriptorModel = assertionDescriptor;
        return this.put<IAssertionDescriptorModel>(ApiRoutes.Assertiondescriptor, {
            body,
        });
    }

    getAssertionDescriptorById(guid: string) {
        if (!guid) {
            const msg = 'Assertion Descriptor GUID not provided.';
            console.error(msg);
            throw new Error(msg);
        }
        return this.get<IAssertionDescriptorExtendedModel>(
            ApiRoutes.AssertionDescriptorDetails.replace('{id}', guid)
        );
    }

    getAssertionDescriptors(
        usageTypeId: number,
        searchString?: string,
        checkForEditAccess?: boolean
    ) {
        const body: any = {
            usageTypeId: usageTypeId,
            searchString: searchString,
            checkForEditAccess: checkForEditAccess,
        };

        return this.post<IAssertionDescriptorModel[]>(ApiRoutes.AssertionDescriptors, {
            body,
        });
    }

    getUserDetails(guid: string) {
        return this.get<IUserModel>(ApiRoutes.UserDetails.replace('{id}', guid));
    }

    getUsers(active: boolean, searchString?: string) {
        let route = ApiRoutes.User + '?active=' + active.toString();
        if (searchString) route += '&searchString=' + searchString;

        return this.get<IUserModel[]>(route);
    }

    createUserAccount(userInformation: IRegistrationModel) {
        const body: IRegistrationModel = userInformation;
        return this.post<IRegistrationModel>(ApiRoutes.User, { body });
    }

    createInvitation(userInvitation: IInvitationModel) {
        const body: IInvitationModel = userInvitation;
        return this.post<IResponseModel<IInvitationModel>>(ApiRoutes.UserInvitation, { body });
    }

    getUserInvitationByEmail(userInvitation: IInvitationModel) {
        const body: IInvitationModel = userInvitation;
        return this.post<IResponseModel<IInvitationModel>>(ApiRoutes.GetUserInvitation, { body });
    }

    getUserInvitation(guid: string) {
        return this.get<IInvitationModel>(ApiRoutes.UserInvitationDetails.replace('{id}', guid));
    }

    // ACCOUNT / USER
    loginAcceptInvitation(login: ILoginUser, invitationGuid: string) {
        const body: ILoginUser = login;
        return this.post<ILoginUser>(ApiRoutes.Login + '?invitationGuid=' + invitationGuid, {
            body,
        });
    }

    loginVerifyTwoFactorCode(login: ILoginUser, twoFactorToken: string) {
        const body: ILoginUser = login;
        return this.post<ILoginResponse>(ApiRoutes.Login + '?twoFactorToken=' + twoFactorToken, {
            body,
        });
    }

    recoverAccount(recoverAccount: IRecoverAccount) {
        const body: IRecoverAccount = recoverAccount;
        return this.post<IRecoverAccount>(ApiRoutes.AccountRecover, { body });
    }

    resetPassword(userId: string, code: string, password: string) {
        return this.post<IResponse>(ApiRoutes.ResetPassword, {
            body: { id: userId, code: code, password: password },
        });
    }

    login(login: ILoginUser) {
        const body: ILoginUser = login;
        return this.post<ILoginResponse>(ApiRoutes.Login, { body });
    }

    acceptTermsOfService(userGuid: string, userAgreementContentGuid: string, login: ILoginUser) {
        const body: ILoginUser = login;
        return this.post<ILoginResponse>(
            ApiRoutes.AcceptTermsOfService.replace('{id}', userGuid).replace(
                '{userAgreementContentGuid}',
                userAgreementContentGuid
            ),
            {
                body,
            }
        );
    }

    getLoggedInUserInfo() {
        return this.get<IUserModel>(ApiRoutes.LoggedInUserDetails);
    }

    //Assertion Descriptor Api Calls

    getAssertionDescriptorLevels() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/threatassessmentlevels');
    }

    getAssertionDescriptorDisplayCategories() {
        return this.get<IAssertionDescriptorDisplayCategoryLookupModel[]>(
            ApiRoutes.Lookup + '/assertiondescriptordisplaycategories'
        );
    }

    // case Api Calls

    getCaseFileStaticItems() {
        return this.get<IStaticItemLookupModel[]>(ApiRoutes.Lookup + '/CaseFileStaticItems');
    }

    getCaseFileDisplayCategories() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/AssertionDescriptorDisplayCategories');
    }

    getDocumentTemplates() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/documenttemplates');
    }

    getDocumentTemplateById(guid: string) {
        return this.get<string>(ApiRoutes.DocumentTemplate.replace('{id}', guid));
    }

    // Report Api Calls

    getReportOptions() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/threatreportoptions');
    }

    // User zone profile Api Calls

    getUserZoneProfile() {
        return this.get<IUserZoneProfileModel>(ApiRoutes.UserZoneProfile);
    }

    getUserSetting(type?: number, defaultOrg?: boolean) {
        let url = ApiRoutes.UserSetting;
        if (type) {
            url = url + '?type=' + type;
            if (defaultOrg) url = url + '&defaultOrg=true';
        } else {
            if (defaultOrg) url = url + '?defaultOrg=true';
        }

        return this.get<IUserSettingModel[]>(url);
    }

    saveUserSetting(userSetting: IUserSettingModel) {
        const body: IUserSettingModel = userSetting;
        return this.post<IUserSettingModel>(ApiRoutes.UserSetting, { body });
    }

    getUserZoneProfiles(search: IUserZoneProfileSearchModel) {
        const body: IUserZoneProfileSearchModel = search;
        return this.post<IUserZoneProfileModel[]>(ApiRoutes.UserZoneProfiles, {
            body,
        });
    }

    createUserZoneProfile(userZoneProfile: IUserZoneProfileModel) {
        const body: IUserZoneProfileModel = userZoneProfile;
        return this.post<IUserZoneProfileModel>(ApiRoutes.UserZoneProfile, {
            body,
        });
    }

    updateUserZoneProfile(userZoneProfile: IUserZoneProfileModel) {
        const body: IUserZoneProfileModel = userZoneProfile;
        return this.put<IUserZoneProfileModel>(ApiRoutes.UserZoneProfile, { body });
    }

    getUserZoneProfileRoleTypes() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/userzoneprofileroles');
    }

    getUserZoneProfileSkillTypes() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/skills');
    }

    getUserZoneProfileSkillLevelTypes() {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + '/skilllevels');
    }

    getDocumentCategoriesForEntityType(entityTypeId: number) {
        const url = ApiRoutes.LookupEntityType.replace(
            '{entityTypeId}',
            entityTypeId.toString()
        ).replace('{lookupType}', 'documentcategorytypes');
        return this.get<ILookupModel[]>(url);
    }

    getNotes(typeId: number, key?: string) {
        let url = ApiRoutes.Notes + '?typeId=' + typeId.toString();
        if (key) url += '&key=' + key;
        return this.get<INoteModel[]>(url);
    }

    logout() {
        return this.post<ILoginUser>(ApiRoutes.Logout);
    }

    updateUserDetails(userInformation: IUserModel) {
        const body: IUserModel = userInformation;
        return this.put<IUserModel>(ApiRoutes.User, { body });
    }

    getAlertBarInfo() {
        return this.get<IAlertSummaryModel[]>(ApiRoutes.UserAlertSummary);
    }

    getOrganizations(searchString?: string, organizationTypes?: number[]) {
        let route = ApiRoutes.Organization;
        if (searchString && searchString !== '') {
            route += '?searchString=' + searchString;
        }
        if (organizationTypes && organizationTypes.length > 0) {
            route += '?types=' + organizationTypes.join(',');
        }
        return this.get<IOrganizationModel[]>(route);
    }

    createOrganization(organization: IOrganizationModel) {
        const body: IOrganizationModel = organization;
        return this.post<IOrganizationModel>(ApiRoutes.Organization, { body });
    }

    updateOrganization(organization: IOrganizationModel) {
        const body: IOrganizationModel = organization;
        return this.put<IOrganizationModel>(ApiRoutes.Organization, { body });
    }

    updateUserSetting(userSetting: IUserSettingModel) {
        const body: IUserSettingModel = userSetting;
        return this.post<IUserSettingModel>(ApiRoutes.UserSetting, { body });
    }

    getUserSettingById(userSettingId: string) {
        return this.get<IUserSettingModel>(ApiRoutes.UserSetting + '/' + userSettingId);
    }

    getUserSettingByType(type: number) {
        return this.get<IUserSettingModel>(ApiRoutes.UserSetting + '?type=' + type);
    }

    getViewSameCases(searchModel: ISameCaseMatchedCasesSearchModel) {
        return this.post<IPagedSortedSearchResponseModel<ISameCaseModel, SameCaseSortColumns>>(
            ApiRoutes.SameCasesView,
            {
                body: searchModel,
            }
        );
    }

    searchSameCasesManage(searchModel: ISameCaseMatchedCasesSearchModel) {
        return this.post<
            IPagedSortedSearchResponseModel<ISameCaseMatchResponseModel, SameCaseSortColumns>
        >(ApiRoutes.SameCaseManageAdmin, {
            body: searchModel,
        });
    }

    searchCaseResolutionCases(searchModel: ICaseResolutionSearchModel) {
        searchModel.includedSteps = true;
        return this.post<IPagedSortedSearchResponseModel<ICaseResolutionModel, undefined>>(
            ApiRoutes.CaseResolutionSearch,
            {
                body: searchModel
            }
        )
    }

    seedDatabaseCaseResolution() {
        return this.post("api/case-resolution-plans/bulk-create");
    }

    createCaseResolutionCasePlan(casePlan: CreateCaseResolutionPlanRequest) {
        const body: CreateCaseResolutionPlanRequest = casePlan
        return this.post<CreateCaseResolutionPlanRequest>(
            ApiRoutes.CaseResolutionCreate,
            {
                body
            }
        )
    }

    getCaseResolutionCase(caseId: string, isCaseId?: boolean) {
        return this.get<ICaseResolutionModel>(ApiRoutes.CaseResolutionGet.replace('{id}', caseId), {
            queryParams: isCaseId ? { isCaseId: true } : undefined
        });
    }

    updateCaseResolutionCase(caseId: string, model: ICaseResolutionModel) {
        return this.post<any>(
            ApiRoutes.CaseResolutionGet.replace('{id}', caseId),
            {
                body: model,
            }
        );
    }

    deleteCaseResolutionCase(caseId: string) {
        return this.delete<any>(ApiRoutes.CaseResolutionGet.replace('{id}', caseId));
    }

    updateCaseResolutionStep(caseId:string, stepId:string) {
        return this.put<any>(
            ApiRoutes.CaseResolutionStepUpdate.replace('{id}', caseId).replace('{step-id}', stepId));
    }
    
    approveCaseResolutionStep(caseId: string, stepId: string, stepData: ICaseResolutionPlanStepDataModel) {
        return this.post<any>(ApiRoutes.CaseResolutionStepApprove.replace('{caseResolutionPlanId}', caseId).replace('{stepId}', stepId),
        {
            body: stepData
        });
    }

    rejectCaseResolutionStep(caseId: string, stepId: string) {
        return this.post<any>(ApiRoutes.CaseResolutionStepReject.replace('{caseResolutionPlanId}', caseId).replace('{stepId}', stepId));
    }

    getCaseResolutionStepHistory(caseId: string, stepId: string) {
        return this.get<ICaseResolutionPlanStepHistoryModel[]>(ApiRoutes.CaseResolutionStepHistory.replace('{caseResolutionPlanId}', caseId).replace('{stepId}', stepId));
    }

    getCaseResolutionStepHistoryAll(caseId: string) {
        return this.get<any>(ApiRoutes.CaseResolutionStepHistoryAll.replace('{caseResolutionPlanId}', caseId));
    }

    setCaseResolutionUnpinned(caseId: string) {
        return this.delete<any>(ApiRoutes.CaseResolutionSetPinned.replace('{id}', caseId));
    }

    setCaseResolutionPinned(caseId: string) {
        return this.post<any>(
            ApiRoutes.CaseResolutionSetPinned.replace("{id}", caseId)
        )
    }
    getCaseManagersByUser() {
        return this.get<ICaseResolutionCaseManager[]>(ApiRoutes.CaseManagersByUser);
    }

    getPlaintiffFirmsByUser() {
        return this.get<ICaseResolutionPlaintiffFirms[]>(ApiRoutes.PlaintiffFirmsByUser);
    }

    getNationalPlaintiffFirmsByUser() {
        return this.get<ICaseResolutionPlaintiffFirms[]>(ApiRoutes.NationalPlaintiffFirmsByUser);
    }

    searchSameCaseCasesAdmin(searchModel: ISameCaseMatchedCasesSearchModel) {
        return this.post<
            IPagedSortedSearchResponseModel<IMatchedCasesSearchResponseModel, SameCaseSortColumns>
        >(ApiRoutes.SameCaseMatchedCasesSearchAdmin, {
            body: searchModel,
        });
    }

    searchSameCasesAdmin(searchModel: ISameCaseMatchedCasesSearchModel) {
        return this.post<IPagedSortedSearchResponseModel<ISameCaseModel, SameCaseSortColumns>>(
            ApiRoutes.SameCaseSearchAdmin,
            {
                body: searchModel,
            }
        );
    }

    saveSameCase(saveModel: ISaveSameCaseModel) {
        return this.post<ISameCaseMatchResponseModel>(ApiRoutes.SameCaseSaveAdmin, {
            body: saveModel,
        });
    }

    rejectCaseSameCase(rejectModel: IRejectCaseSameCaseModel) {
        return this.post<IRejectCaseSameCaseModel>(
            ApiRoutes.SameCaseRejectAdmin.replace(
                '{sameCaseGuid}',
                rejectModel.sameCaseGuid
            ).replace('{caseGuid}', rejectModel.caseGuid)
        );
    }

    processAllSameCases() {
        return this.post<IResponse>(ApiRoutes.SameCaseProcessAllMatchesAdmin);
    }

    createZoneNote(quadZoneNoteRequest: IQuadZoneNoteRequest) {
        return this.post<IResponse>(ApiRoutes.QuadCreateZoneNote, {
            body: quadZoneNoteRequest,
        });
    }

    getQuadZoneNoteTargets(organizationGuids: string[] | undefined) {
        const queryString = organizationGuids
            ?.map((g) => `organizationGuids=${encodeURIComponent(g)}`)
            .join('&');
        return this.get<IUserModel[]>(ApiRoutes.QuadZoneNoteTargets + `?${queryString}`);
    }

    getUserCaseFileModels() {
        return this.get<ICaseFileModel[]>(ApiRoutes.GetUserCaseFileModels);
    }

    getCenterCaseAssertionDescriptors(caseGroupGuid: string) {
        return this.get<IAssertionDescriptorModel[]>(
            ApiRoutes.SameCaseAssertionDescriptors.replace('{sameCaseGuid}', caseGroupGuid)
        );
    }

    getCenterStaticItems(caseGroupGuid: string) {
        return this.get<IStaticItemLookupModel[]>(
            ApiRoutes.SameCaseStaticItems.replace('{sameCaseGuid}', caseGroupGuid)
        );
    }

    getQuadAssertionDescriptorsByAuthorizedZones(
        caseFileModelId: string,
        organizationGuids?: string[]
    ) {
        const queryString =
            organizationGuids && organizationGuids.length > 0
                ? `?${organizationGuids
                      .map((g) => `organizationGuids=${encodeURIComponent(g)}`)
                      .join('&')}`
                : '';

        return this.get<IAssertionDescriptorModel[]>(
            `${ApiRoutes.QuadAssertionDescriptorsByAuthorizedZones.replace('{caseFileModelId}', caseFileModelId)}${queryString}`
        );
    }

    getQuadCaseStaticItemsByAuthorizedZones(caseFileModelId: string, organizationGuids?: string[]) {
        const queryString =
            organizationGuids && organizationGuids.length > 0
                ? `?${organizationGuids
                      .map((g) => `organizationGuids=${encodeURIComponent(g)}`)
                      .join('&')}`
                : '';

        return this.get<IStaticItemLookupModel[]>(
            `${ApiRoutes.QuadCaseStaticItemsByAuthorizedZones.replace('{caseFileModelId}', caseFileModelId)}${queryString}`
        );
    }

    createQuadTrigger(trigger: IQuadTriggerRuleSettingsModel) {
        return this.post<IQuadTriggerRuleSettingsModel>(ApiRoutes.QuadTriggerRuleCreate, {
            body: trigger,
        });
    }

    getSameCaseEasyUpdate(caseGroupGuid: string) {
        return this.get<ISameCaseEasyUpdateModel>(
            ApiRoutes.SameCasesEasyUpdateGet.replace('{sameCaseGuid}', caseGroupGuid)
        );
    }

    saveSameCaseEasyUpdate(sameCaseGuid: string, model: ISameCaseEasyUpdateModel) {
        return this.put<ISameCaseEasyUpdateResponseModel>(
            ApiRoutes.SameCasesEasyUpdate.replace('{sameCaseGuid}', sameCaseGuid),
            {
                body: model,
            }
        );
    }

    getQuadCaseManagersInZone(zoneGuid: string) {
        return this.getQuadZoneUsers(zoneGuid, DataScopesEnum.National.Value);
    }

    getQuadZoneUsers(zoneGuid: string, dataScopeId?: number) {
        const url = new URL(
            window.location.origin +
                '/' +
                ApiRoutes.QuadZoneUsers.replace('{organizationGuid}', zoneGuid)
        );

        if (dataScopeId) {
            url.searchParams.append('dataScope', dataScopeId.toString());
        }

        return this.get<IUserModel[]>(url.href);
    }

    getOrganizationsInZone(zoneGuid: string, organizationTypeId: number) {
        const url = new URL(
            window.location.origin +
                '/' +
                ApiRoutes.QuadOrganizationDependenciesByOrganization.replace(
                    '{organizationGuid}',
                    zoneGuid
                )
        );

        if (organizationTypeId) {
            url.searchParams.append('organizationTypeId', organizationTypeId.toString());
        }

        return this.get<IOrganizationModel[]>(url.href);
    }

    getQuadCaseQueryCount(querySettings: IQuerySettingsModel) {
        const body: IQuerySettingsModel = querySettings;
        return this.post<IResponseModel<number>>(ApiRoutes.QuadQuery + '?count=true', {
            body,
        });
    }

    getQuadCaseQueryResults<T extends IQueryResultModel | ICaseExpertModel[]>(
        querySettings: IQuerySettingsModel
    ) {
        return this.post<T>(ApiRoutes.QuadQuery, { body: querySettings });
    }

    getQuadCaseNotesQueryResults(querySettings: IQuerySettingsModel) {
        const body: IQuerySettingsModel = querySettings;
        return this.post<INoteModel[]>(ApiRoutes.QuadQuery, { body });
    }

    getQuadDepositionQueryResults(querySettings: IQuerySettingsModel) {
        const body: IQuerySettingsModel = querySettings;
        return this.post<IDepositionModel[]>(ApiRoutes.QuadQuery, { body });
    }

    searchQuadNotes(searchModel: INoteUISearchModel) {
        const body: INoteUISearchModel = searchModel;
        return this.post<INoteModel[]>(ApiRoutes.QuadNotes, { body });
    }

    getSameCase(sameCaseGuid: string, caseFileModelId: number) {
        return this.get<ISameCaseModel>(
            ApiRoutes.SameCaseGet.replace('{sameCaseGuid}', sameCaseGuid).replace(
                '{caseFileModelId}',
                caseFileModelId.toString()
            )
        );
    }

    checkCaseSameCase(caseGuid: string) {
        return this.get<ISameCaseCaseModel>(
            ApiRoutes.SameCaseCheck.replace('{caseGuid}', caseGuid)
        );
    }

    getQuadCaseAuditLogs(guid: string) {
        let url = ApiRoutes.SameCaseAuditLogs.replace('{caseGuid}', guid);
        return this.get<ICaseAuditLog[]>(url);
    }
}

export default CheckmateApiClient;

export const transformRequestOptions = (params: any) => {
    let options: any[] = [];
    for (const key in params) {
        if (typeof params[key] !== 'object' && params[key] !== undefined) {
            options.push(`${key}=${encodeURIComponent(params[key])}`);
        } else if (Array.isArray(params[key])) {
            const arrayOptions = params[key]
                ?.filter((arrayItem: any) => arrayItem !== null && arrayItem !== undefined)
                .map((el: any) => `${key}=${encodeURIComponent(el?.value || el)}`);
            options = options.concat(arrayOptions);
        } else if (params[key] !== undefined) {
            const value = params[key];
            if (value) {
                options.push(`${key}=${encodeURIComponent(value?.value || value)}`);
            }
        }
    }
    return options.join('&');
};
